import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { getToc, refreshToc, type TocRefreshResponse, type TocResponse } from '../api/toc';

/**
 * The TOC tree changes only when an ingest writes files, so the server caches it and the client never auto-refetches.
 * The cache is invalidated server-side after each ingest, and the admin "Refresh TOC" button (which calls
 * `useRefreshToc`) invalidates both server and client caches for a manual refresh.
 */
export function useToc() {
  return useQuery<TocResponse>({
    queryKey: ['toc'],
    queryFn: getToc,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useRefreshToc(): UseMutationResult<TocRefreshResponse, Error, void> {
  const queryClient = useQueryClient();
  return useMutation<TocRefreshResponse, Error, void>({
    mutationFn: refreshToc,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['toc'] });
    },
  });
}
